import { admissionDatabaseRequests } from "@/src/api/admissionOfficeDatabaseRequests";
import { applicantHistoryRequests } from "@/src/api/applicantHistoryRequests";
import { tasksPageRequests } from "@/src/api/tasksPageRequests";
import { tasksStatusConst } from "@/src/constants/tasksConsts";
import { matchAuthDocByPdfPath } from "@/src/utils/admissionOffice/matchAuthDocByPdfPath";
import { parseAuthDocsResponse } from "@/src/utils/admissionOffice/parseAuthDocsResponse";

const IN_PROCESS_STATUS = "in_process";

export interface ExecuteDocumentCheckToCorrectParams {
  fullName: string;
  attachedFile: string;
  taskId: string;
  applicantId?: number;
  lookupDocumentIdByPdfPath?: (pdfPath: string) => Promise<number | null>;
}

export type ExecuteDocumentCheckToCorrectResult =
  | { ok: true; message: string }
  | { ok: false; message: string };

/** Deletes attached document and moves task back to In Process for correction. */
export const executeDocumentCheckToCorrect = async ({
  fullName,
  attachedFile,
  taskId,
  applicantId,
  lookupDocumentIdByPdfPath,
}: ExecuteDocumentCheckToCorrectParams): Promise<ExecuteDocumentCheckToCorrectResult> => {
  const trimmedFile = attachedFile.trim();
  if (!trimmedFile) {
    return { ok: false, message: "Attached document path is missing." };
  }

  let documentId: number | null = null;
  const authRawResponse = await admissionDatabaseRequests.getAuthDocsByFullname(fullName);
  const authDocs = parseAuthDocsResponse(authRawResponse);
  const matchedDoc = matchAuthDocByPdfPath(authDocs, trimmedFile);
  if (typeof matchedDoc?.id === "number") {
    documentId = matchedDoc.id;
  } else if (lookupDocumentIdByPdfPath) {
    documentId = await lookupDocumentIdByPdfPath(trimmedFile);
  }

  const userOffice = localStorage.getItem("userID") || "without login";
  if (documentId != null) {
    const deleted = await admissionDatabaseRequests.deleteCurrentDocument(documentId, userOffice);
    if (deleted == null) {
      return { ok: false, message: "Could not delete the document from Admissions Office." };
    }
  } else {
    console.warn("[DocumentCheckToCorrect] Document id not found; updating task only.", trimmedFile);
  }

  await tasksPageRequests.update(taskId, {
    status: IN_PROCESS_STATUS,
    attached_file: null,
  });

  const inProcessLabel = tasksStatusConst.find((item) => item.id === IN_PROCESS_STATUS)?.label ?? IN_PROCESS_STATUS;

  if (applicantId != null && Number.isFinite(applicantId)) {
    try {
      await applicantHistoryRequests.create({
        applicant_id: applicantId,
        text: `Changes have been made to status updated to "${inProcessLabel}"`,
      });
    } catch (error) {
      console.warn("[DocumentCheckToCorrect] History create failed:", error);
    }
  }

  return {
    ok: true,
    message: `Document removed. Task marked as "${inProcessLabel}".`,
  };
};
